(ns ui.wallet.plan
  "The strip above the wallet panels for an existing monthly or yearly
   subscription: what keeps renewing, its status, resume and cancel. The
   subscription itself can no longer be changed; buying credits and Auto
   Top-Up happen in the same `ui.wallet.on-demand` panels every subscriber
   gets.

   Texts are `[:i18n ...]` references, resolved by `ui.wallet.page`."
  (:require [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.ui.button :as button]
            [storrito.wallet :as wallet]
            [ui.elements]
            [ui.subscription.core :as core]))

(defn subscription-interval
  [paddle-subscription]
  (case (get-in paddle-subscription [:billing_cycle :interval])
    "year" :year
    :month))

(defn status-badge
  [paddle-subscription]
  (let [[label css] (cond
                      (:scheduled_change paddle-subscription)
                      [[:i18n :top-up/status-cancellation-scheduled]
                       ["bg-orange-100" "text-orange-800"]]

                      (= "past_due" (:status paddle-subscription))
                      [[:i18n :top-up/status-payment-failed]
                       ["bg-red-100" "text-red-800"]]

                      :else
                      [[:i18n :top-up/status-active]
                       ["bg-green-100" "text-green-800"]])]
    [:span {:class (concat ["inline-block" "rounded-full" "px-3" "py-1"
                            "text-xs" "font-medium"]
                           css)}
     label]))

(defn upgrade-hint
  "`:suggest-upgrade` of the subscription query: the top-up does not cover
   the connected Instagram accounts until the next billing date. The old
   subscription page reacted with a big yellow warning and a suggested
   upgrade table; now one sentence recommends Auto Top-Up or buying
   credits in the panels below."
  [{:keys [upgrade?]} paddle-subscription]
  (when upgrade?
    [:p.mt-3.text-sm.text-yellow-800
     [:i18n :plan/wont-cover
      {:date (subs (or (:next_billed_at paddle-subscription) "") 0 10)}]
     " "
     [:i18n :plan/wont-cover-advice]]))

(def resume-command
  {:command/kind :command/subscription-resume
   :command/data {}})

(def switch-command
  {:command/kind :command/wallet-switch-on-demand
   :command/data {}})

(defn switch-button
  "Converts the monthly or yearly subscription to on-demand top-ups
   without a new checkout: the item is swapped to the $0 price, nothing
   is charged, credits and payment method stay. Also the way out of a
   scheduled cancellation."
  [w paddle-subscription]
  (let [issued? (command/issued? w switch-command)]
    [:button
     {:class button/button-secondary-css
      :disabled (boolean issued?)
      :on {:click [[:js/confirm
                    [:i18n :plan/switch-confirm
                     {:quantity (wallet/subscription-amount paddle-subscription)}]]
                   [:data/command
                    switch-command
                    {:on-success (concat core/query-actions
                                         [core/repeat-import-and-refresh])}]]}}
     (if issued?
       [:i18n :plan/switching]
       [:i18n :plan/switch])]))

(defn actions
  [w paddle-subscription]
  (if (:scheduled_change paddle-subscription)
    (list
      [:button
       {:class button/button-primary-css
        :disabled (command/issued? w resume-command)
        :on {:click [[:data/command
                      resume-command
                      {:on-success [core/import-and-refresh]}]]}}
       [:i18n :top-up/resume]]
      (switch-button w paddle-subscription))
    (list
      (switch-button w paddle-subscription)
      [:button
       {:class ["text-sm" "text-red-600" "hover:underline"]
        :on {:click [[:ui/modal-open :modals/subscription-cancel-modal {}]]}}
       [:i18n :top-up/cancel]])))

(defn plan-panel
  [w]
  (let [subscription (query/get-result w core/subscription-query)
        paddle-subscription (:organization/paddle-subscription subscription)
        item (first (:items paddle-subscription))
        cancellation (:scheduled_change paddle-subscription)]
    [:div {:class ui.elements/panel}
     [:div.flex.items-center.justify-between
      ;; Not "Auto Top-Up": that name now belongs to the threshold top-up
      ;; in the panel below.
      [:h2 {:class ui.elements/h3}
       [:i18n (case (subscription-interval paddle-subscription)
                :year :plan/title-yearly
                :plan/title-monthly)]]
      (status-badge paddle-subscription)]
     [:p.mt-3.text-2xl.font-semibold.text-gray-900
      [:i18n (case (subscription-interval paddle-subscription)
               :year :top-up/per-year
               :top-up/per-month)
       {:quantity (:quantity item)}]]
     [:p.text-sm.text-gray-500
      (if cancellation
        [:i18n :top-up/stops-on
         {:date (subs (or (:effective_at cancellation) "") 0 10)}]
        [:i18n :top-up/next-on
         {:date (subs (or (:next_billed_at paddle-subscription) "") 0 10)}])]
     ;; No hint while a cancellation is scheduled: there is no next
     ;; top-up date to warn about.
     (when-not cancellation
       (upgrade-hint (:suggest-upgrade subscription) paddle-subscription))
     (when (= "past_due" (:status paddle-subscription))
       [:p.mt-3.text-sm.text-red-600
        [:i18n :top-up/past-due]
        [:a.underline {:href "/subscription/invoices"
                       :target "_blank"}
         [:i18n :top-up/update-payment-method]]
        "."])
     [:div.mt-4.flex.items-center.gap-3
      (actions w paddle-subscription)]
     (when-let [error (or (core/command-error w switch-command [:i18n :page/command-failed])
                          (core/command-error w resume-command [:i18n :page/command-failed]))]
       [:p.mt-2.text-sm.text-red-500 error])]))

(defn paddle-unavailable
  []
  [:div {:class ui.elements/panel}
   [:h2 {:class ui.elements/h3} [:i18n :top-up/title]]
   [:p {:class (concat ui.elements/p-sm ["mt-2"])}
    [:i18n :top-up/paddle-unavailable]]
   [:button
    {:class (concat button/button-secondary-css ["mt-4"])
     :on {:click core/query-actions}}
    [:i18n :top-up/try-again]]])

